import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { SortDirection, type Workspace } from '@air/api/types';

import { usePrivateBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePrivateBoardsFetcher';

export const PRIVATE_GENERAL_LIBRARY_BOARDS_LIST_KEY = 'PRIVATE_GENERAL_LIBRARY_BOARDS_LIST';

const PRIVATE_GENERAL_LIBRARY_BOARDS_PAGE_LIMIT = 100;

export const getPrivateGeneralLibraryBoardsListKey = (params: UsePrivateGeneralLibraryBoardsListParams) => {
  return [PRIVATE_GENERAL_LIBRARY_BOARDS_LIST_KEY, params];
};

export type UsePrivateGeneralLibraryBoardsListParams = {
  workspaceId: Workspace['id'] | undefined;
};

export type UsePrivateGeneralLibraryBoardsListOptions = {
  enabled?: boolean;
};

/**
 * Root-level workspace boards that are not assigned to any library — the "general" section of the
 * workspace when content is grouped by library.
 */
export const usePrivateGeneralLibraryBoardsList = (
  params: UsePrivateGeneralLibraryBoardsListParams,
  options?: UsePrivateGeneralLibraryBoardsListOptions,
) => {
  const { workspaceId } = params;
  const { privateBoardsFetcher } = usePrivateBoardsFetcher();

  return useInfiniteQuery({
    queryKey: getPrivateGeneralLibraryBoardsListKey(params),
    queryFn: !workspaceId
      ? skipToken
      : ({ pageParam }) =>
          privateBoardsFetcher({
            params: {
              libraryBoards: 'NO',
              view: 'root',
              limit: PRIVATE_GENERAL_LIBRARY_BOARDS_PAGE_LIMIT,
              includeDirectDescendantBoardCount: true,
              includeCoverImage: true,
              numThumbnails: 1,
            },
            sortFieldName: 'customSort',
            sortDirection: SortDirection.desc,
            cursor: pageParam || null,
          }),
    enabled: !!workspaceId && options?.enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });
};
